import { useState } from "react";

export default function App() {
  const [legalOpen, setLegalOpen] = useState<string | null>(null); // 'privacy' | 'legal' | null

  const calendlyLink = "https://calendly.com/hello-klaudiastrugala/1h";
  const emailLink = "mailto:hello@klaudiastrugala.com";

  const sections = [
    {
      title: "Clarity Session",
      price: "€90",
      description:
        "A focused 1:1 session to turn your fashion brand idea into a clear, realistic plan.",
      bullets: [
        "What product to start with",
        "Pricing and positioning direction",
        "Production strategy in Europe",
        "Clear next steps after the call",
      ],
      cta: "Start your brand →",
      link: calendlyLink,
    },
    {
      title: "Production & Sourcing",
      price: "From €500",
      description:
        "A premium sourcing service for founders who want the right manufacturer and fabric direction without wasting months searching.",
      bullets: [
        "Tailored manufacturer research",
        "Curated sourcing options",
        "Guidance on selection",
        "Support through the process",
      ],
      cta: "Contact me →",
      link: emailLink,
    },
  ];

  return (
    <div className="page">
      <header className="header">
        <div className="container header-inner">
          <div className="brand">
            <img src="/ks-logo.png" alt="KS logo" className="brand-logo" />
            <div className="brand-name">Klaudia Strugała</div>
          </div>

          <nav className="nav">
            <a href="#services">Services</a>
            <a href="#process">Process</a>
            <a href="#contact">Contact</a>
          </nav>

          <a
            href={calendlyLink}
            target="_blank"
            rel="noreferrer"
            className="button button-outline small-hide"
          >
            Start your brand →
          </a>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="container hero-inner">
            <div className="eyebrow">Fashion business consultant</div>
            <h1>Build your fashion brand in Europe — the right way.</h1>
            <p className="hero-text">
              From idea to production — I help you choose the right product,
              pricing and manufacturer so you don’t waste months or make costly
              mistakes.
            </p>

            <div className="button-row">
              <a href="#services" className="button button-dark">
                Explore services
              </a>
              <a
                href={calendlyLink}
                target="_blank"
                rel="noreferrer"
                className="button button-light"
              >
                Start your brand →
              </a>
            </div>

            <p className="hero-note">
              Limited availability — I work with a small number of brands at a
              time.
            </p>
          </div>
        </section>

        <section className="problem">
          <div className="container">
            <h2>Starting a fashion brand shouldn’t feel this confusing.</h2>
            <div className="card-grid">
              {[
                "Which country makes sense?",
                "What prices are realistic?",
                "Who can you trust?",
                "Where do you even start?",
              ].map((item) => (
                <div key={item} className="soft-card">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="services" className="services">
          <div className="container">
            <h2>Ways to work with me</h2>
            <div className="service-grid">
              {sections.map((section) => (
                <div key={section.title} className="service-card">
                  <div className="service-top">
                    <h3>{section.title}</h3>
                    <div className="price-pill">{section.price}</div>
                  </div>

                  <p className="service-description">{section.description}</p>

                  <ul className="bullet-list">
                    {section.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>

                  <a
                    href={section.link}
                    target="_blank"
                    rel="noreferrer"
                    className="button button-outline"
                  >
                    {section.cta}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="process" className="process">
          <div className="container">
            <h2>A clear process from idea to production.</h2>
            <div className="process-grid">
              {[
                "You share your idea",
                "We build direction",
                "I source selectively",
                "You move forward clearly",
              ].map((step) => (
                <div key={step} className="process-card">
                  {step}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="contact">
          <div className="container contact-inner">
            <h2>Ready to start your brand?</h2>
            <p>Let’s build it properly from the beginning.</p>
            <a href={emailLink} className="button button-dark">
              Contact me →
            </a>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="footer">
          <div className="container footer-inner">
            <p>© {new Date().getFullYear()} Klaudia Strugała</p>
            <div className="footer-links">
              <button
                onClick={() => setLegalOpen("privacy")}
                className="link-button"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => setLegalOpen("legal")}
                className="link-button"
              >
                Legal Notice
              </button>
            </div>
          </div>
        </footer>

        {/* MODAL */}
        {legalOpen && (
          <div className="legal-modal">
            <div
              className="legal-box"
              style={{ maxHeight: "80vh", overflowY: "auto" }}
            >
              <button className="close-btn" onClick={() => setLegalOpen(null)}>
                ×
              </button>

              {legalOpen === "privacy" && (
                <>
                  <h2>Privacy Policy</h2>

                  <p>
                    This Privacy Policy explains how personal data is collected,
                    used and protected when you use this website.
                  </p>

                  <p>
                    <strong>Data Controller:</strong>
                    <br />
                    Favi Klaudia Strugała
                    <br />
                    NIP: PL1182305779
                    <br />
                    Warsaw, Poland
                    <br />
                    Email: hello@klaudiastrugala.com
                  </p>

                  <h3>What data we collect</h3>
                  <p>
                    Personal data may be collected when you contact us via email
                    or book a session via Calendly. This may include your name,
                    email address and any information you choose to provide.
                  </p>

                  <h3>How we use your data</h3>
                  <p>
                    Your data is used only to respond to your inquiries, provide
                    consulting services, and manage bookings.
                  </p>

                  <h3>Third-party services</h3>
                  <p>
                    We use third-party tools to operate this website:
                    <br />• Calendly – for scheduling sessions
                    <br />• Stripe – for secure payment processing
                  </p>

                  <p>
                    These providers may process your data according to their own
                    privacy policies.
                  </p>

                  <h3>Legal basis</h3>
                  <p>
                    Data is processed based on your consent and the necessity to
                    perform a service requested by you.
                  </p>

                  <h3>Your rights</h3>
                  <p>
                    You have the right to access, correct or delete your
                    personal data. You may contact us at any time to exercise
                    these rights.
                  </p>
                </>
              )}

              {legalOpen === "legal" && (
                <>
                  <h2>Legal Notice</h2>

                  <p>
                    <strong>Operator of the website:</strong>
                  </p>

                  <p>
                    Favi Klaudia Strugała
                    <br />
                    NIP: PL1182305779
                    <br />
                    Warsaw, Poland
                    <br />
                    Email: hello@klaudiastrugala.com
                  </p>

                  <h3>Purpose of the website</h3>
                  <p>
                    This website provides information about consulting services
                    related to fashion brand creation, production and sourcing.
                  </p>

                  <h3>Liability</h3>
                  <p>
                    The content of this website is provided for informational
                    purposes only. While efforts are made to ensure accuracy, no
                    guarantees are made regarding completeness or correctness.
                  </p>

                  <h3>Intellectual property</h3>
                  <p>
                    All content on this website, including text, branding and
                    visuals, is the property of Favi Klaudia Strugała and may
                    not be used without permission.
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
